'use client';

import React from 'react';
import { useIntl } from 'react-intl';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { H2 } from '@osmosis/tailwind-ui/components/Text';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import { borderNeutral200 } from '@osmosis/tailwind-ui/styles/border';
import { text5 } from '@osmosis/tailwind-ui/styles/text';

type Props = {
  /** Section title shown on the left of the header. */
  title: string;
  /** Invoked when the user taps the Hide button to close the overlay. */
  onHide: () => void;
  className?: string;
};

/**
 * Header bar rendered at the top of a mobile section overlay. Shows the section
 * title and a "Hide" button that closes the overlay. Generic — used by any
 * non-AI section (the AI panel ships its own header/Hide button).
 */
const MobileOverlayHeader = ({
  title,
  onHide,
  className,
}: Props): JSX.Element => {
  const { formatMessage } = useIntl();

  const hideLabel = formatMessage({
    id: 'ai-panel.hide',
    defaultMessage: 'Hide',
  });

  return (
    <Flex
      className={cn(
        'shrink-0 items-center justify-between border-b px-4 py-2',
        borderNeutral200,
        className,
      )}
    >
      <H2 className={cn('!text-lg !font-bold', text5)}>{title}</H2>
      <Button
        variant="transparent"
        color="transparent"
        startIcon={<Icon icon={faXmark} size={15} className={text5} />}
        data-testid="mobile-overlay-hide-btn"
        aria-label={hideLabel}
        className={cn(text5, '!px-2 text-sm font-bold hover:bg-blue-100')}
        onClick={onHide}
      >
        {hideLabel}
      </Button>
    </Flex>
  );
};

export default MobileOverlayHeader;
